import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCcw, MessageSquare, Search } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

type Ticket = {
  id: string;
  user_id: string;
  user_email: string | null;
  workspace_id: string | null;
  category: string;
  subject: string;
  body: string;
  page_path: string | null;
  user_agent: string | null;
  status: string;
  admin_notes: string | null;
  resolved_at: string | null;
  created_at: string;
};

const STATUS_TONE: Record<string, string> = {
  open: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  in_progress: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  resolved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  closed: "bg-muted text-muted-foreground",
};

export function FeedbackTab() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [draftStatus, setDraftStatus] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke(
      `admin-feedback-list?status=${statusFilter}&category=${categoryFilter}`,
      { method: "GET" },
    );
    if (error) {
      toast.error(error.message);
    } else {
      setTickets((data as { tickets: Ticket[] }).tickets ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return tickets;
    const q = search.toLowerCase();
    return tickets.filter(
      (t) =>
        t.subject.toLowerCase().includes(q) ||
        t.body.toLowerCase().includes(q) ||
        (t.user_email ?? "").toLowerCase().includes(q),
    );
  }, [tickets, search]);

  const openTicket = (t: Ticket) => {
    setSelected(t);
    setDraftStatus(t.status);
    setDraftNotes(t.admin_notes ?? "");
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("admin-feedback-update", {
      body: { ticket_id: selected.id, status: draftStatus, admin_notes: draftNotes },
    });
    if (error) {
      toast.error(error.message);
    } else {
      const updated = (data as { ticket: Ticket }).ticket;
      setTickets((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated, user_email: t.user_email } : t)));
      setSelected({ ...selected, ...updated, user_email: selected.user_email });
      toast.success("Saved");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" /> Feedback inbox
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                  <SelectItem value="idea">Idea</SelectItem>
                  <SelectItem value="question">Question</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-48">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 pl-7" />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tickets.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">Category</th>
                    <th className="py-2 pr-3">Subject</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr key={t.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/50" onClick={() => openTicket(t)}>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">
                        {new Date(t.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2 pr-3 text-xs">{t.user_email || t.user_id.slice(0, 8)}</td>
                      <td className="py-2 pr-3"><Badge variant="outline">{t.category}</Badge></td>
                      <td className="py-2 pr-3 max-w-md truncate">{t.subject}</td>
                      <td className="py-2 pr-3">
                        <Badge className={STATUS_TONE[t.status] ?? ""}>{t.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{selected?.subject}</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-4 text-sm">
              <div className="text-xs text-muted-foreground">
                From {selected.user_email ?? selected.user_id} •{" "}
                {new Date(selected.created_at).toLocaleString()}
              </div>
              <div className="rounded-md border bg-muted/30 p-3 whitespace-pre-wrap">{selected.body}</div>
              {selected.page_path && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Page:</span> {selected.page_path}
                </div>
              )}
              {selected.user_agent && (
                <div className="text-xs text-muted-foreground break-all">UA: {selected.user_agent}</div>
              )}
              <div>
                <Label>Status</Label>
                <Select value={draftStatus} onValueChange={setDraftStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Admin notes (internal)</Label>
                <Textarea value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} rows={4} maxLength={5000} />
              </div>
              <Button onClick={() => void save()} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
